import React, {
  useCallback, useState, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';

import GearIcon from '../../../assets/gear.svg';
import { UserRole } from '../../../../shared/enums';
import { useTooltip } from '../../hooks';
import ToggleEditAccess from './ToggleEditAccess';

import styles from '../../styles/views/components/EditingSettings.module.scss';

const ALL_ROLES = Object.values(UserRole);
const ACCESS_TO = { EDIT: 'edit', DOWNLOAD: 'Download' };

const EditingSettings = ({
  isShown,
  isExpandView,
  pageTitle,
  tooltipTextForCustomerEdit,
  tooltipTextForWriterEdit,
  tooltipTextForContentTeamEdit,
  tooltipTextForCustomerDownload,
  isCustomerEnableToEdit,
  isWriterEnableToEdit,
  isContentTeamEnableToEdit,
  isDevelopmentalEditorEnableToEdit,
  isProProofreaderEnableToEdit,
  isCustomerEnableToDownload,
  wrapperExtraStyles,
  onToggleEdit,
  onToggleDownload,
  withDownloadActions,
  isShowContentTeamSection,
  isShowDevelopmentalEditorSection,
  isShowProProofreaderSection,
  isDisabledDevelopmentalEditorSection,
  isDisabledProProofreaderSection,
}) => {
  useTooltip();
  const settingMenuWrapperRef = useRef(null);
  const gearIconRef = useRef(null);
  const [isEditSettingOpen, setIsEditSettingOpen] = useState(false);

  const toggleEditSetting = () => setIsEditSettingOpen((prev) => !prev);

  const handleOnToggleUserAccess = useCallback((role, action = ACCESS_TO.EDIT) => () => {
    if (!ALL_ROLES.includes(role)) return;
    const userRole = role[0].toUpperCase() + role.slice(1);
    if (action === ACCESS_TO.EDIT) onToggleEdit(userRole);
    else if (action === ACCESS_TO.DOWNLOAD) onToggleDownload(userRole);
  }, [onToggleEdit, onToggleDownload]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isEditSettingOpen
          && settingMenuWrapperRef
          && !settingMenuWrapperRef.current.contains(event.target)
          && gearIconRef
          && !gearIconRef.current.contains(event.target)
      ) {
        setIsEditSettingOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditSettingOpen]);

  return isShown ? (
    <div className={styles.settingsWrapper} style={wrapperExtraStyles}>
      <GearIcon
        ref={gearIconRef}
        onClick={toggleEditSetting}
        className={isEditSettingOpen ? styles.animateGear : styles.gear}
        data-tip={isExpandView ? t('components.editingSettings', { ns: i18next.Namespace.COMMON }) : ''}
      />
      {!isExpandView && <span>{t('components.editingSettings', { ns: i18next.Namespace.COMMON })}</span>}
      {isEditSettingOpen && (
        <div ref={settingMenuWrapperRef} className={styles.editWrapper}>
          <ToggleEditAccess
            isUserEnableToEdit={isCustomerEnableToEdit}
            onToggleEditAccess={handleOnToggleUserAccess(UserRole.CUSTOMER)}
            description={t('components.enableCustomerEditAccess', { ns: i18next.Namespace.COMMON, pageTitle })}
            tooltipText={tooltipTextForCustomerEdit}
          />
          <ToggleEditAccess
            isUserEnableToEdit={isWriterEnableToEdit}
            onToggleEditAccess={handleOnToggleUserAccess(UserRole.WRITER)}
            description={t('components.enableWriterEditAccess', { ns: i18next.Namespace.COMMON, pageTitle })}
            tooltipText={tooltipTextForWriterEdit}
          />
          {withDownloadActions && (
            <ToggleEditAccess
              isUserEnableToEdit={isCustomerEnableToDownload}
              onToggleEditAccess={handleOnToggleUserAccess(UserRole.CUSTOMER, ACCESS_TO.DOWNLOAD)}
              description={t('components.enableCustomerDownloadAccess', { ns: i18next.Namespace.COMMON, pageTitle })}
              tooltipText={tooltipTextForCustomerDownload}
            />
          )}
          {isShowContentTeamSection && (
            <ToggleEditAccess
              isUserEnableToEdit={isContentTeamEnableToEdit}
              onToggleEditAccess={handleOnToggleUserAccess(UserRole.CONTENT_TEAM)}
              description={t('components.enableContentTeamEditAccess', { ns: i18next.Namespace.COMMON, pageTitle })}
              tooltipText={tooltipTextForContentTeamEdit}
            />
          )}
          {isShowDevelopmentalEditorSection && (
            <ToggleEditAccess
              isUserEnableToEdit={isDevelopmentalEditorEnableToEdit}
              onToggleEditAccess={handleOnToggleUserAccess(UserRole.DEVELOPMENTAL_EDITOR)}
              description={t('components.enableDevelopmentalEditorEditAccess', { ns: i18next.Namespace.COMMON, pageTitle })}
              tooltipText={isDisabledDevelopmentalEditorSection ? t('disabledDevelopmentalEditorForThisProject') : t('toggleTooltipDevelopmentalEditorEdit')}
              isDisabled={isDisabledDevelopmentalEditorSection}
            />
          )}
          {isShowProProofreaderSection && (
            <ToggleEditAccess
              isUserEnableToEdit={isProProofreaderEnableToEdit}
              onToggleEditAccess={handleOnToggleUserAccess(UserRole.PRO_PROOFREADER)}
              description={t('components.enableProProofreaderEditAccess', { ns: i18next.Namespace.COMMON, pageTitle })}
              tooltipText={isDisabledDevelopmentalEditorSection ? t('disabledProProofreaderForThisProject') : t('toggleTooltipProProofreaderEdit')}
              isDisabled={isDisabledProProofreaderSection}
            />
          )}
        </div>
      )}
    </div>
  ) : null;
};

EditingSettings.propTypes = {
  isShown: PropTypes.bool.isRequired,
  isExpandView: PropTypes.bool,
  pageTitle: PropTypes.string.isRequired,
  tooltipTextForWriterEdit: PropTypes.string.isRequired,
  tooltipTextForCustomerEdit: PropTypes.string.isRequired,
  tooltipTextForCustomerDownload: PropTypes.string,
  tooltipTextForContentTeamEdit: PropTypes.string.isRequired,
  isCustomerEnableToEdit: PropTypes.bool,
  isWriterEnableToEdit: PropTypes.bool,
  isCustomerEnableToDownload: PropTypes.bool,
  wrapperExtraStyles: PropTypes.shape({}),
  onToggleEdit: PropTypes.func.isRequired,
  onToggleDownload: PropTypes.func,
  withDownloadActions: PropTypes.bool,
  isShowContentTeamSection: PropTypes.bool,
  isShowDevelopmentalEditorSection: PropTypes.bool,
  isShowProProofreaderSection: PropTypes.bool,
  isDisabledDevelopmentalEditorSection: PropTypes.bool.isRequired,
  isDisabledProProofreaderSection: PropTypes.bool.isRequired,
  isContentTeamEnableToEdit: PropTypes.bool.isRequired,
  isDevelopmentalEditorEnableToEdit: PropTypes.bool.isRequired,
  isProProofreaderEnableToEdit: PropTypes.bool.isRequired,
};

EditingSettings.defaultProps = {
  isExpandView: false,
  wrapperExtraStyles: {},
  withDownloadActions: false,
  isCustomerEnableToEdit: false,
  isWriterEnableToEdit: false,
  isCustomerEnableToDownload: false,
  tooltipTextForCustomerDownload: '',
  onToggleDownload: () => {},
  isShowContentTeamSection: false,
  isShowDevelopmentalEditorSection: false,
  isShowProProofreaderSection: false,
};

export default EditingSettings;
